import { API } from 'aws-amplify';
import { getAuthToken } from '../../utils/AuthUtils';
import { useCallback } from 'react';

export const useQuestionnaireTrackingAPI = (surveyYear, companyId, participantId) => {
    const fetchQuestionnaireRecord = useCallback(async () => {
        const authToken = await getAuthToken();
        const path = `/survey-questionnaire-submission/records/${surveyYear}/${companyId}/${participantId}`;

        try {
            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Content-Type': 'application/json',
                    'Token': authToken
                }
            });
            return { success: true, data: response };
        } catch (error) {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving questionnaire tracking data. Please try again later.");
            }
            return { 
                success: false, 
                error: "An error occurred retrieving questionnaire tracking data"
            };
        }
    }, [surveyYear, companyId, participantId]);

    const fetchParticipantMetadata = useCallback(async () => {
        const authToken = await getAuthToken();
        const path = `/unrestricted/survey/participants?id=${participantId}`;
        
        try {
            const response = await API.get('UnrestrictedSurveyData', path, {
                headers: {
                    'Token': authToken
                }
            });
            return { success: true, data: response };
        } catch (error) {
            console.log(error);
            return { success: false, error: "Failed to fetch participant metadata" };
        }
    }, [participantId]);

    const updateTrackingRecordStatus = useCallback(async (trackingRecord) => {
        try {
            const authToken = await getAuthToken();
            const path = `/survey-questionnaire-submission/records/${surveyYear}/${companyId}/${participantId}`;
            const queryStringParameters = {
                assignedTo: trackingRecord.assignedTo,
                processingStatus: trackingRecord.processingStatus,
                sensitive: trackingRecord.sensitive,
                companyName: trackingRecord.participantName
            }

            await API.put('AnnualCompensations', path, {
                headers: {
                    'Token': authToken
                },
                queryStringParameters
            });
            
            return { success: true };
        } catch (error) {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while updating questionnaire tracking data. Please try again later.");
            }
            return { success: false, error: "Failed to update tracking record status" };
        }
    }, [surveyYear, companyId, participantId]);

    const getSubmissionFile = useCallback(async (fileKey) => {
        if (!fileKey) return { success: false, error: "No file key provided" };

        const authToken = await getAuthToken();
        const path = `/restricted/survey/files?fileKey=${encodeURIComponent(fileKey)}`;

        try {
            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Token': authToken
                }
            });
            return { success: true, data: response };
        } catch (error) {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving the submission file. Please try again later.");
            }
            return { success: false, error: "An error occurred downloading the submission file" };
        }
    }, []);

    const getProcessedFile = useCallback(async (processedFileKey) => {
        if (!processedFileKey) return { success: false, error: "No processed file key provided" };

        const authToken = await getAuthToken();
        const path = `/restricted/survey/processed-files?fileKey=${encodeURIComponent(processedFileKey)}`;

        try {
            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Token': authToken
                }
            });
            return { success: true, data: response };
        } catch (error) {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving the processed file. Please try again later.");
            }
            return { success: false, error: "An error occurred downloading the processed file" };
        }
    }, []);

    const fetchLatestSubmissionRecord = useCallback(async () => {
        const authToken = await getAuthToken();
        const path = `/restricted/survey/${participantId}/latest-submission`;

        try {
            const response = await API.get('AnnualCompensations', path, {
                headers: {
                    'Token': authToken
                }
            });
            return { success: true, data: response };
        } catch (error) {
            if (error?.response?.data?.message) {
                console.log(error.response.data.message);
            } else {
                console.log(error);
                console.log("An error occurred while retrieving the submission record. Please try again later.");
            }
            return { success: false, error: "An error occurred retrieving the submission record" };
        }
    }, [participantId]);

    return {
        fetchQuestionnaireRecord,
        fetchParticipantMetadata,
        updateTrackingRecordStatus,
        getSubmissionFile,
        getProcessedFile,
        fetchLatestSubmissionRecord
    };
}; 